/**
 * Company Admin Billing Portal
 *
 * Allows company admins to manage their subscription, view invoices,
 * update email preferences, and monitor license usage.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Subscription {
  id: string;
  companyId: string;
  stripeSubscriptionId: string;
  stripePriceId: string;
  status: string;
  trialEndDate: Date | null;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
}

interface BillingBreakdown {
  baseTierName: string;
  baseTierRange: string;
  baseTierAmount: number;
  hasOverflow: boolean;
  overflowCount: number;
  overflowPerSeatRate: number;
  overflowAmount: number;
  totalAmount: number;
  effectiveLicenseCount?: number;
}

interface BillingPlan {
  name: string;
  tierRange: string;
  monthlyPrice: number;
  baseMonthlyPrice?: number;
  licenseCount: number;
  billedCount?: number;
  seatUsingCount?: number;
  rateType: string;
  breakdown: BillingBreakdown;
  activeDiscount?: {
    percentOff?: number;
    amountOff?: number;
    name?: string;
  } | null;
}

interface PendingChanges {
  pendingLicenseCount: number | null;
  pendingRateType: string | null;
}

interface Invoice {
  id: string;
  number: string;
  amount_paid: number;
  amount_due: number;
  status: string;
  created: number;
  invoice_pdf: string;
  hosted_invoice_url: string;
}

interface EmailPreferences {
  enablePaymentNotifications: boolean;
  enableInvoiceNotifications: boolean;
  billingEmail: string | null;
}

interface LicensePreview {
  currentBreakdown: BillingBreakdown;
  newBreakdown: BillingBreakdown;
  priceDifferenceCents: number;
  priceDifferenceDollars: string;
  effectiveNextBillingCycle: boolean;
  requestedLicenseCount: number;
  effectiveLicenseCount?: number;
}

interface LicenseUsage {
  totalLicenses: number;
  usedLicenses: number;
  availableLicenses: number;
  activeUsers: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    consumesLicense?: boolean;
  }>;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export const CompanyBilling: React.FC = () => {
  const { user, refreshAccessToken } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [billingPlan, setBillingPlan] = useState<BillingPlan | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [emailPreferences, setEmailPreferences] = useState<EmailPreferences>({
    enablePaymentNotifications: true,
    enableInvoiceNotifications: true,
    billingEmail: null,
  });
  const [desiredLicenseCount, setDesiredLicenseCount] = useState<number | null>(null);
  const [licensePreview, setLicensePreview] = useState<LicensePreview | null>(null);
  const [licensePreviewLoading, setLicensePreviewLoading] = useState(false);
  const [licenseUpdateLoading, setLicenseUpdateLoading] = useState(false);
  const [showLicenseConfirm, setShowLicenseConfirm] = useState(false);
  const [licenseUsage, setLicenseUsage] = useState<LicenseUsage | null>(null);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'preferences' | 'usage'>('overview');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchBillingData();
  }, [user, navigate]);

  // Initialize desired license count from usage data
  useEffect(() => {
    if (licenseUsage && desiredLicenseCount === null) {
      setDesiredLicenseCount(licenseUsage.totalLicenses);
    }
  }, [licenseUsage, desiredLicenseCount]);

  const fetchLicensePreview = useCallback(async (count: number) => {
    try {
      setLicensePreviewLoading(true);
      const token = localStorage.getItem('riskninja-token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/billing/subscription/license-preview?licenseCount=${count}`,
        config
      );
      setLicensePreview(response.data);
    } catch (error: any) {
      console.error('Error fetching license preview:', error);
      setLicensePreview(null);
    } finally {
      setLicensePreviewLoading(false);
    }
  }, []);

  // Debounced license preview
  const handleLicenseCountChange = useCallback((newCount: number) => {
    if (!licenseUsage) return;
    const minCount = licenseUsage.usedLicenses || 1;
    const clamped = Math.max(minCount, newCount);
    setDesiredLicenseCount(clamped);
    setLicensePreview(null);

    if (clamped !== licenseUsage.totalLicenses) {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => fetchLicensePreview(clamped), 500);
    }
  }, [licenseUsage, fetchLicensePreview]);

  const handleUpdateLicenses = async () => {
    if (!desiredLicenseCount || !licenseUsage) return;
    try {
      setLicenseUpdateLoading(true);
      setMessage(null);
      const token = localStorage.getItem('riskninja-token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(
        `${API_BASE_URL}/api/admin/billing/subscription/licenses`,
        { licenseCount: desiredLicenseCount },
        config
      );
      setMessage({ type: 'success', text: `License change to ${desiredLicenseCount} queued for next billing cycle` });
      setShowLicenseConfirm(false);
      setLicensePreview(null);
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error updating licenses:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update license count',
      });
    } finally {
      setLicenseUpdateLoading(false);
    }
  };

  const fetchBillingData = async (retryCount = 0): Promise<void> => {
    try {
      setLoading(true);

      const token = localStorage.getItem('riskninja-token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [subRes, planRes, invoicesRes, prefsRes, usageRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/billing/subscription`, config),
        axios.get(`${API_BASE_URL}/api/admin/billing/plan`, config),
        axios.get(`${API_BASE_URL}/api/admin/billing/invoices`, config),
        axios.get(`${API_BASE_URL}/api/admin/billing/email-preferences`, config),
        axios.get(`${API_BASE_URL}/api/admin/billing/usage`, config),
      ]);

      setSubscription(subRes.data.subscription);
      setBillingPlan(planRes.data.plan);
      setInvoices(invoicesRes.data.invoices || []);
      setEmailPreferences(prefsRes.data);
      setLicenseUsage(usageRes.data);
      setPendingChanges(subRes.data.pendingChanges || null);
    } catch (error: any) {
      console.error('Error fetching billing data:', error);

      // If 403 error and we haven't retried yet, try refreshing the token
      if (error.response?.status === 403 && retryCount === 0) {
        console.log('🔄 Token invalid, attempting refresh...');
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          console.log('✅ Token refreshed, retrying request...');
          return fetchBillingData(1); // Retry once
        }
      }

      if (error.response?.status === 404) {
        // No subscription yet
        setSubscription(null);
      } else {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load billing data',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCustomerPortal = async () => {
    try {
      const token = localStorage.getItem('riskninja-token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const response = await axios.post(`${API_BASE_URL}/api/admin/billing/customer-portal`, {
        returnUrl: window.location.href,
      }, config);

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to open customer portal',
      });
    }
  };

  const handleStartCheckout = async () => {
    try {
      const token = localStorage.getItem('riskninja-token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const response = await axios.post(`${API_BASE_URL}/api/admin/billing/create-checkout-session`, {
        successUrl: `${window.location.origin}/admin/billing?checkout=success`,
        cancelUrl: `${window.location.origin}/admin/billing?checkout=canceled`,
      }, config);

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error('Error starting checkout:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to start checkout',
      });
    }
  };

  const handleSaveEmailPreferences = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const token = localStorage.getItem('riskninja-token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      await axios.patch(`${API_BASE_URL}/api/admin/billing/email-preferences`, emailPreferences, config);

      setMessage({
        type: 'success',
        text: 'Email preferences saved successfully!',
      });
    } catch (error: any) {
      console.error('Error saving email preferences:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save email preferences',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const formatCurrencyFromCents = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatDate = (date: Date | number | string | null | undefined): string => {
    if (date === null || date === undefined) return 'N/A';
    const d = typeof date === 'number' ? new Date(date * 1000) : new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600 dark:text-gray-400">Loading billing information...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Billing & Subscription
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your subscription, invoices, and billing preferences
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'invoices', label: 'Invoices' },
              { id: 'preferences', label: 'Email Preferences' },
              { id: 'usage', label: 'License Usage' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Payment Due Banner */}
            {(() => {
              const openInvoice = invoices.find(inv => inv.status === 'open' && inv.amount_due > 0);
              return openInvoice ? (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <h3 className="font-semibold text-red-800 dark:text-red-200">
                          Payment Due
                        </h3>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        You have an outstanding balance of <strong>{formatCurrency(openInvoice.amount_due)}</strong>
                      </p>
                    </div>
                    {openInvoice.hosted_invoice_url && (
                      <a
                        href={openInvoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
                      >
                        Pay Now
                      </a>
                    )}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Pending Changes Banner */}
            {pendingChanges && (pendingChanges.pendingLicenseCount || pendingChanges.pendingRateType) && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                    Pending Changes
                  </h3>
                </div>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  The following changes will take effect on your next billing cycle
                  {subscription?.currentPeriodEnd ? ` (${formatDate(subscription.currentPeriodEnd)})` : ''}:
                </p>
                <ul className="mt-2 text-sm text-amber-700 dark:text-amber-300 space-y-1">
                  {pendingChanges.pendingLicenseCount && (
                    <li>
                      License count changing to <strong>{pendingChanges.pendingLicenseCount}</strong>
                    </li>
                  )}
                  {pendingChanges.pendingRateType && (
                    <li>
                      Rate type changing to <strong>{pendingChanges.pendingRateType === 'discounted' ? 'Discounted' : 'Standard'}</strong>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Subscription Status Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Subscription Status
                </h2>
                {subscription && (
                  <span
                    className={`px-3 py-1 text-sm font-semibold rounded-full ${
                      subscription.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : subscription.status === 'trialing'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}
                  >
                    {subscription.status}
                  </span>
                )}
              </div>

              {subscription && billingPlan ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Current Plan
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {billingPlan.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      ${billingPlan.monthlyPrice.toFixed(2)}/month • Billed seats: {billingPlan.billedCount ?? billingPlan.licenseCount}
                    </p>
                    {billingPlan.seatUsingCount !== undefined && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {billingPlan.seatUsingCount} seat-using of {billingPlan.licenseCount} licensed
                        {billingPlan.billedCount !== billingPlan.seatUsingCount && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">override active</span>
                        )}
                        {' · '}
                        <button
                          type="button"
                          onClick={() => navigate('/admin')}
                          className="text-primary hover:underline"
                        >
                          manage users
                        </button>
                      </p>
                    )}
                    {billingPlan.activeDiscount && (
                      <div className="mt-1 text-xs text-purple-600 dark:text-purple-400">
                        {billingPlan.activeDiscount.percentOff
                          ? `${billingPlan.activeDiscount.percentOff}% discount applied`
                          : billingPlan.activeDiscount.amountOff
                          ? `$${billingPlan.activeDiscount.amountOff.toFixed(2)} discount applied`
                          : 'Discount applied'}
                        {billingPlan.baseMonthlyPrice && billingPlan.baseMonthlyPrice !== billingPlan.monthlyPrice && (
                          <span className="ml-1 line-through text-gray-400">
                            ${billingPlan.baseMonthlyPrice.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                    {billingPlan.breakdown && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                        <p>Base: {billingPlan.breakdown.baseTierName} ({billingPlan.breakdown.baseTierRange}) - {formatCurrencyFromCents(billingPlan.breakdown.baseTierAmount)}</p>
                        {billingPlan.breakdown.hasOverflow && (
                          <p>
                            + {billingPlan.breakdown.overflowCount} overflow seats x {formatCurrencyFromCents(billingPlan.breakdown.overflowPerSeatRate)}/seat = {formatCurrencyFromCents(billingPlan.breakdown.overflowAmount)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Billing Cycle
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {formatDate(subscription.currentPeriodStart)} -{' '}
                      {formatDate(subscription.currentPeriodEnd)}
                    </p>
                    {subscription.trialEndDate && (
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                        Trial ends: {formatDate(subscription.trialEndDate)}
                      </p>
                    )}
                  </div>

                  {subscription.cancelAtPeriodEnd && (
                    <div className="col-span-2 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        Your subscription will be canceled at the end of the current billing period
                        ({formatDate(subscription.currentPeriodEnd)})
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No active subscription found
                  </p>
                  <button
                    onClick={handleStartCheckout}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    Subscribe Now
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    You'll be redirected to Stripe's secure checkout page.
                  </p>
                </div>
              )}

              {subscription && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-3">
                  {(() => {
                    const openInvoice = invoices.find(inv => inv.status === 'open' && inv.amount_due > 0);
                    if (openInvoice?.hosted_invoice_url) {
                      return (
                        <>
                          <a
                            href={openInvoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors text-center"
                          >
                            Pay Now - {formatCurrency(openInvoice.amount_due)}
                          </a>
                          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            Pay your outstanding balance and manage payment methods
                          </p>
                        </>
                      );
                    }
                    return (
                      <>
                        <button
                          onClick={handleOpenCustomerPortal}
                          className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                        >
                          Manage Payment Methods
                        </button>
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          Update payment methods or cancel your subscription
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* License Usage Summary */}
            {licenseUsage && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  License Usage
                </h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {licenseUsage.totalLicenses}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Total Licenses
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {licenseUsage.usedLicenses}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Active Users</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {licenseUsage.availableLicenses}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Available Licenses
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* License Management */}
            {licenseUsage && subscription && desiredLicenseCount !== null && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Manage Licenses
                </h2>

                <div className="flex items-center gap-4 mb-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    License Count
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleLicenseCountChange(desiredLicenseCount - 1)}
                      disabled={desiredLicenseCount <= (licenseUsage.usedLicenses || 1)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={desiredLicenseCount}
                      onChange={(e) => handleLicenseCountChange(parseInt(e.target.value, 10) || 1)}
                      min={licenseUsage.usedLicenses || 1}
                      className="w-20 text-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={() => handleLicenseCountChange(desiredLicenseCount + 1)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      +
                    </button>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    (min: {licenseUsage.usedLicenses || 1} active users)
                  </span>
                </div>

                {licensePreviewLoading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Calculating changes...
                  </p>
                )}

                {licensePreview && desiredLicenseCount !== licenseUsage.totalLicenses && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 mb-4 space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        Effective next billing cycle
                      </p>
                    </div>

                    {/* Tier unlock notice */}
                    {licensePreview.effectiveLicenseCount && licensePreview.effectiveLicenseCount > desiredLicenseCount && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <svg className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-green-700 dark:text-green-300">
                          Tier unlock: {desiredLicenseCount} licenses qualifies for {licensePreview.newBreakdown.baseTierName} — you get <strong>{licensePreview.effectiveLicenseCount} licenses</strong> at the flat rate
                        </p>
                      </div>
                    )}

                    {/* Current vs New Breakdown */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Current</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrencyFromCents(licensePreview.currentBreakdown.totalAmount)}/mo
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {licensePreview.currentBreakdown.baseTierName}
                          {licensePreview.currentBreakdown.effectiveLicenseCount && (
                            <> ({licensePreview.currentBreakdown.effectiveLicenseCount} licenses)</>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">New</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrencyFromCents(licensePreview.newBreakdown.totalAmount)}/mo
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {licensePreview.newBreakdown.baseTierName}
                          {licensePreview.newBreakdown.hasOverflow ? (
                            <> + {licensePreview.newBreakdown.overflowCount} overflow ({desiredLicenseCount} licenses)</>
                          ) : licensePreview.newBreakdown.effectiveLicenseCount ? (
                            <> ({licensePreview.newBreakdown.effectiveLicenseCount} licenses)</>
                          ) : null}
                        </p>
                      </div>
                    </div>

                    {licensePreview.priceDifferenceCents !== 0 && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 pt-1 border-t border-gray-200 dark:border-gray-700">
                        <span className="font-medium">Monthly change:</span>{' '}
                        <span className={licensePreview.priceDifferenceCents > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                          {licensePreview.priceDifferenceCents > 0 ? '+' : ''}
                          {formatCurrencyFromCents(licensePreview.priceDifferenceCents)}
                        </span>
                      </p>
                    )}
                  </div>
                )}

                {desiredLicenseCount !== licenseUsage.totalLicenses && !showLicenseConfirm && (
                  <button
                    onClick={() => setShowLicenseConfirm(true)}
                    disabled={licensePreviewLoading}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    Update Licenses
                  </button>
                )}

                {showLicenseConfirm && (
                  <div className="border border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                      Are you sure you want to change from {licenseUsage.totalLicenses} to{' '}
                      {licensePreview?.effectiveLicenseCount && licensePreview.effectiveLicenseCount > desiredLicenseCount
                        ? `${licensePreview.effectiveLicenseCount} licenses (tier unlock from ${desiredLicenseCount} requested)`
                        : `${desiredLicenseCount} licenses`}?
                      This change will take effect on your next billing cycle.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleUpdateLicenses}
                        disabled={licenseUpdateLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        {licenseUpdateLoading ? 'Updating...' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => {
                          setShowLicenseConfirm(false);
                          setDesiredLicenseCount(licenseUsage.totalLicenses);
                          setLicensePreview(null);
                        }}
                        disabled={licenseUpdateLoading}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Invoice History
              </h2>
            </div>
            {invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {invoice.number || `INV-${invoice.id.substring(0, 8)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(invoice.created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {formatCurrency(invoice.amount_paid || invoice.amount_due)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              invoice.status === 'paid'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : invoice.status === 'open'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}
                          >
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-2">
                            {invoice.invoice_pdf && (
                              <a
                                href={invoice.invoice_pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                PDF
                              </a>
                            )}
                            {invoice.hosted_invoice_url && (
                              <a
                                href={invoice.hosted_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                View
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                No invoices found
              </div>
            )}
          </div>
        )}

        {/* Email Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Email Notification Preferences
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Billing Email Address
                </label>
                <input
                  type="email"
                  value={emailPreferences.billingEmail || ''}
                  onChange={(e) =>
                    setEmailPreferences({ ...emailPreferences, billingEmail: e.target.value })
                  }
                  placeholder="Leave blank to use company admin email"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="paymentNotifications"
                    checked={emailPreferences.enablePaymentNotifications}
                    onChange={(e) =>
                      setEmailPreferences({
                        ...emailPreferences,
                        enablePaymentNotifications: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label
                    htmlFor="paymentNotifications"
                    className="ml-3 text-sm text-gray-700 dark:text-gray-300"
                  >
                    Payment notifications (successful and failed payments)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="invoiceNotifications"
                    checked={emailPreferences.enableInvoiceNotifications}
                    onChange={(e) =>
                      setEmailPreferences({
                        ...emailPreferences,
                        enableInvoiceNotifications: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label
                    htmlFor="invoiceNotifications"
                    className="ml-3 text-sm text-gray-700 dark:text-gray-300"
                  >
                    Invoice notifications (upcoming and finalized invoices)
                  </label>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={handleSaveEmailPreferences}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* License Usage Tab */}
        {activeTab === 'usage' && licenseUsage && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              License Usage Details
            </h2>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {licenseUsage.totalLicenses}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Licenses</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {licenseUsage.usedLicenses}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Seat-Using Users</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {licenseUsage.availableLicenses}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Available Licenses
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Active Users ({licenseUsage.activeUsers.length})
              </h3>
              <div className="space-y-2">
                {licenseUsage.activeUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.consumesLicense === false && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          Not using a seat
                        </span>
                      )}
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                        Active
                      </span>
                    </div>
                  </div>
                ))}
                {licenseUsage.activeUsers.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No active users</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyBilling;
